import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
    BadgeCheck,
    CheckCircle2,
    CircleAlert,
    ExternalLink,
    MessageSquareText,
    Mic,
    MicOff,
    ShieldCheck,
    Star,
    ThumbsUp,
    UsersRound,
} from 'lucide-react'
import { getServerUrl } from '../../utils/serverHours'

const FEEDBACK_API_URL = 'https://devapi.kanchaneshver.com/api/EnquiryMaster/AddFeedbackSurvey'

const registrationLinks = {
    dsa: 'https://api.kanchaneshver.com/api/CamNoteDetail/self-DSA-register',
    banker: 'https://api.kanchaneshver.com/api/CamNoteDetail/self-banker-register-redirect',
}

const uspPoints = [
    {
        title: 'Multiple Products, Multiple Income Sources',
        text: 'Work across home loan, LAP, business loan, used car loan, personal and professional loan, credit card, insurance, CIBIL, soft sanction, eSign and eStamp.',
        result: 'One customer can create multiple earning opportunities.',
    },
    {
        title: 'Ready Customer Data',
        text: 'Profiling calls from a large Indian mobile app user base can be converted into business leads.',
        result: 'Less effort and faster business results.',
    },
    {
        title: 'No Need for a Diary',
        text: 'The digital lead system saves reminders, dates, times and complete follow-up history inside the mobile app.',
        result: 'No follow-up gets missed.',
    },
    {
        title: 'LEH - Work From Anywhere',
        text: 'Loan Exchange HUB helps your expert team coordinate operations and support deal closures across India.',
        result: 'Income can continue even when you are not personally closing every case.',
    },
    {
        title: 'Lifetime Customer Protection',
        text: 'Once a customer becomes connected with you, no one else can work on that customer without your permission or NOC.',
        result: 'Your hard work stays protected.',
    },
    {
        title: 'Complete Business Privacy',
        text: 'Only you can view your business, leads and customer activity.',
        result: 'A secure and trustworthy operating system.',
    },
    {
        title: 'Instant Online Earnings',
        text: 'Earn through CIBIL report, soft sanction, eSign and eStamp with auto invoice, transparent payout and vendor sharing.',
        result: 'Digital services create instant earning options.',
    },
    {
        title: 'Smart Get Bank System',
        text: 'The system recommends suitable banks according to customer profile, policy fit and approval possibility.',
        result: 'Right bank selection saves time and improves approval chances.',
    },
    {
        title: 'Mobile App for Everyone',
        text: 'DSAs can track leads and income, customers can track applications, and bankers can review documentation faster.',
        result: 'Everything stays digital and trackable.',
    },
    {
        title: 'Powerful Career Growth Structure',
        text: 'Branch, area, cluster, region and corporate growth paths support targets, incentives and MIS visibility.',
        result: 'Growth happens with a clear plan.',
    },
    {
        title: 'Simple Subscription Model',
        text: 'A low monthly subscription model helps users begin earning without heavy initial pressure.',
        result: 'Low risk and strong business opportunity.',
    },
    {
        title: '24/7 Support System',
        text: 'Dedicated operations team, software training and support assistance help users focus on business.',
        result: 'You focus on growth while backend operations get support.',
    },
]

const featureOptions = [
    'Lead management',
    'Bank matching',
    'Customer protection',
    'Payout transparency',
    'Mobile app experience',
    'Support team',
    'Document workflow',
    'Career growth structure',
]

const FeedbackSurvey = () => {
    const { type } = useParams()
    const surveyType = type === 'banker' ? 'banker' : 'dsa'
    const surveyLabel = surveyType === 'banker' ? 'Banker' : 'DSA'
    const recognitionRef = useRef(null)

    const [rating, setRating] = useState(0)
    const [selectedFeatures, setSelectedFeatures] = useState([])
    const [loading, setLoading] = useState(false)
    const [isListening, setIsListening] = useState(false)
    const [status, setStatus] = useState({ type: '', message: '' })
    const voiceSupported =
        typeof window !== 'undefined' &&
        ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)

    const [form, setForm] = useState({
        fullName: '',
        phoneNumber: '',
        email: '',
        city: '',
        feedbackMessage: '',
    })

    const ratingLabel = useMemo(() => {
        if (!rating) return 'Select your rating'
        if (rating <= 3) return 'Needs urgent improvement'
        if (rating <= 6) return 'Can be improved'
        if (rating <= 8) return 'Good experience'
        return 'Excellent experience'
    }, [rating])

    const isLowRating = rating > 0 && rating <= 6
    const isHighRating = rating >= 7

    useEffect(() => {
        return () => {
            recognitionRef.current?.stop()
        }
    }, [])

    const handleInputChange = (event) => {
        const { name, value } = event.target

        setForm((prev) => ({
            ...prev,
            [name]: value,
        }))
    }

    const handleFeatureToggle = (feature) => {
        setSelectedFeatures((prev) =>
            prev.includes(feature)
                ? prev.filter((item) => item !== feature)
                : [...prev, feature]
        )
    }

    const handleVoiceInput = () => {
        if (!voiceSupported) {
            setStatus({
                type: 'error',
                message: 'Voice typing is not supported in this browser. Please use Chrome or Edge.',
            })
            return
        }

        if (isListening) {
            recognitionRef.current?.stop()
            setIsListening(false)
            return
        }

        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
        const recognition = new SpeechRecognition()

        recognition.lang = 'en-IN'
        recognition.continuous = true
        recognition.interimResults = false

        recognition.onresult = (event) => {
            const transcript = Array.from(event.results)
                .slice(event.resultIndex)
                .map((result) => result[0]?.transcript)
                .join(' ')
                .trim()

            if (!transcript) return

            setForm((prev) => {
                const currentMessage = prev.feedbackMessage.trimEnd()
                const separator = currentMessage ? ' ' : ''

                return {
                    ...prev,
                    feedbackMessage: `${currentMessage}${separator}${transcript}`,
                }
            })
        }

        recognition.onerror = () => {
            setIsListening(false)
            setStatus({
                type: 'error',
                message: 'Voice input could not start. Please allow microphone permission and try again.',
            })
        }

        recognition.onend = () => {
            setIsListening(false)
        }

        try {
            recognitionRef.current = recognition
            setStatus({ type: '', message: '' })
            setIsListening(true)
            recognition.start()
        } catch (error) {
            setIsListening(false)
            setStatus({
                type: 'error',
                message: 'Voice input could not start. Please try again.',
            })
        }
    }

    const handleSubmit = async (event) => {
        event.preventDefault()

        if (!rating) {
            setStatus({
                type: 'error',
                message: 'Please select a rating before submitting your feedback.',
            })
            return
        }

        if (!form.fullName || !form.phoneNumber || !form.feedbackMessage) {
            setStatus({
                type: 'error',
                message: 'Please fill your name, phone number and feedback message.',
            })
            return
        }

        const payload = {
            Id: 0,
            FeedbackType: `${surveyLabel} Feedback`,
            UserType: surveyType,
            FullName: form.fullName,
            PhoneNumber: form.phoneNumber,
            Email: form.email,
            City: form.city,
            Rating: rating,
            RatingLabel: ratingLabel,
            FeedbackCategory: isLowRating ? 'Improvement Required' : 'Positive Feedback',
            FeedbackMessage: form.feedbackMessage,
            SelectedFeatures: selectedFeatures.join(', '),
            SourcePage: window.location.href,
        }

        try {
            setLoading(true)
            setStatus({ type: '', message: '' })

            const response = await fetch(FEEDBACK_API_URL, {
                method: 'POST',
                headers: {
                    Accept: '*/*',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            })

            const result = await response.json().catch(() => ({}))

            if (!response.ok || result.success === false) {
                throw new Error(result.message || 'Feedback could not be submitted right now.')
            }

            setStatus({
                type: 'success',
                message: 'Thank you. Your feedback has been submitted successfully.',
            })
        } catch (error) {
            setStatus({
                type: 'error',
                message: error.message || 'Something went wrong while submitting feedback.',
            })
        } finally {
            setLoading(false)
        }
    }

    return (
        <main className="min-h-screen bg-[linear-gradient(135deg,#f4f9ff_0%,#ffffff_46%,#fff7f9_100%)] pt-24 pb-16 text-[#1f2633]">
            <section className="container mx-auto px-4 sm:px-8 xl:max-w-6xl xl:px-4">
                <div className="relative overflow-hidden rounded-[34px] border border-[#094D92]/10 bg-white px-6 py-8 text-center shadow-[0_30px_90px_rgba(9,77,146,0.13)] sm:px-8 lg:px-12 reveal-on-scroll contact-reveal-hero">

                    <div className="absolute -left-20 top-8 h-56 w-56 rounded-full bg-[#094D92]/10 blur-3xl" />
                    <div className="absolute -right-20 bottom-4 h-60 w-60 rounded-full bg-[#eb427e]/10 blur-3xl" />
                    <div className="absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-[#094D92]/25 to-transparent" />

                    <div className="relative mx-auto max-w-4xl">
                        <p className="inline-flex items-center gap-2 rounded-full border border-[#094D92]/15 bg-[#f7fbff] px-4 py-2 text-xs font-black uppercase tracking-wide text-[#094D92] shadow-[0_10px_24px_rgba(9,77,146,0.08)]">
                            <MessageSquareText size={16} />
                            {surveyLabel} Feedback Survey
                        </p>

                        <h1 className="mx-auto mt-6 max-w-3xl text-[2rem] font-black leading-[1.08] text-[#202938] sm:text-[2.6rem] lg:text-[3.05rem]">
                            <span className="block">Feedback that helps</span>
                            <span className="block bg-gradient-to-r from-[#094D92] via-[#eb427e] to-[#EDA60C] bg-clip-text text-transparent">
                                Us Serve Better
                            </span>
                        </h1>

                        <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600">
                            Tell us what felt clear, what needs work, and how we can make the loan operating experience smoother for every user.
                        </p>

                        <div className="mx-auto mt-7 grid max-w-3xl gap-3 sm:grid-cols-3">
                            <span className="inline-flex items-center justify-center gap-2 rounded-2xl border border-[#094D92]/15 bg-[#f3f8ff] px-4 py-3 text-sm font-black text-[#094D92] shadow-sm">
                <ShieldCheck size={17} />
              Secure
             </span>

                            <span className="inline-flex items-center justify-center gap-2 rounded-2xl border border-[#eb427e]/10 bg-[#fff4f8] px-4 py-3 text-sm font-black text-[#eb427e] shadow-sm">
                                <UsersRound size={17} />
                                Connected
                            </span>
                            <span className="inline-flex items-center justify-center gap-2 rounded-2xl border border-[#EDA60C]/20 bg-[#fff8e9] px-4 py-3 text-sm font-black text-[#7a4d00] shadow-sm">
                                <BadgeCheck size={17} />
                                Transparent
                            </span>
                        </div>
                    </div>

                    <div className="relative mt-8 grid gap-4 text-left md:grid-cols-2">
                        <div className="flex h-full gap-4 rounded-[26px] border border-[#094D92]/10 bg-[linear-gradient(135deg,#f8fbff_0%,#ffffff_100%)] p-5 shadow-[0_18px_45px_rgba(9,77,146,0.08)]">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                                <CheckCircle2 size={22} />
                            </div>
                            <div>
                                <p className="text-xs font-black uppercase tracking-wide text-[#094D92]">
                                    Improve what matters
                                </p>
                                <p className="mt-2 text-sm font-bold leading-7 text-slate-600">
                                    Your suggestions help us identify issues, improve workflows, and make the platform easier to use.
                                </p>
                            </div>
                        </div>

                        <div className="flex h-full gap-4 rounded-[26px] border border-[#eb427e]/10 bg-[linear-gradient(135deg,#fff7fa_0%,#ffffff_100%)] p-5 shadow-[0_18px_45px_rgba(235,66,126,0.08)]">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#fff0f6] text-[#eb427e]">
                                <ThumbsUp size={22} />
                            </div>
                            <div>
                                <p className="text-xs font-black uppercase tracking-wide text-[#eb427e]">
                                    Continue with confidence
                                </p>
                                <p className="mt-2 text-sm font-bold leading-7 text-slate-600">
                                    Positive feedback can continue directly to the right registration page for the selected user type.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container mx-auto mt-14 px-4 sm:px-8 xl:max-w-6xl xl:px-4">
                <div className="mx-auto mb-8 max-w-3xl text-center reveal-on-scroll">

                    <p className="text-xs font-black uppercase tracking-wide text-[#094D92]">What you are reviewing</p>
                    <h2 className="mt-3 text-3xl font-black leading-tight text-[#202938] md:text-[2.6rem]">
    <span className="block">Built around the moments</span>
    <span className="block bg-gradient-to-r from-[#094D92] to-[#eb427e] bg-clip-text text-transparent">
        that matter in lending
    </span>
</h2>

                    <p className="mt-4 text-base leading-8 text-slate-600">
                        These are the capabilities we want your feedback on, from lead handling and bank matching to protection, payouts, and support.
                    </p>
                </div>

               <div className="grid items-stretch gap-5 md:grid-cols-2 lg:grid-cols-3">

                    {uspPoints.map((item, index) => (
                      <article
    key={item.title}
    className="group relative flex h-full flex-col overflow-hidden rounded-[24px] border border-[#094D92]/10 bg-white p-5 shadow-[0_18px_50px_rgba(9,77,146,0.08)] transition duration-300 hover:-translate-y-1.5 hover:border-[#094D92]/20 hover:shadow-[0_26px_70px_rgba(9,77,146,0.14)] reveal-on-scroll reveal-card"
    style={{ "--reveal-delay": `${index * 70}ms` }}
>
    <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#094D92] via-[#eb427e] to-[#EDA60C]" />
    <div className="mb-5 flex items-center justify-between gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#f3f8ff] text-[#094D92] ring-1 ring-[#094D92]/10 transition group-hover:bg-[#094D92] group-hover:text-white">
            <span className="text-sm font-black">{String(index + 1).padStart(2, '0')}</span>
        </div>
        <BadgeCheck className="text-[#eb427e] opacity-70" size={20} />
    </div>

    <h3 className="min-h-[3.25rem] text-lg font-black leading-snug text-[#202938]">{item.title}</h3>
    <p className="mt-3 text-sm leading-7 text-slate-600">{item.text}</p>

    <div className="mt-auto pt-5">
        <p className="flex min-h-[5.75rem] gap-2 rounded-2xl border border-emerald-100 bg-emerald-50/80 p-3 text-sm font-bold leading-6 text-emerald-700">
            <CheckCircle2 className="mt-0.5 shrink-0" size={17} />
            {item.result}
        </p>
    </div>
</article>

                    ))}
                </div>
            </section>

            <section className="container mx-auto mt-16 px-4 sm:px-8 xl:max-w-5xl xl:px-4">
                <form
    onSubmit={handleSubmit}
    className="relative overflow-hidden rounded-[36px] border border-[#094D92]/12 bg-white shadow-[0_34px_100px_rgba(31,38,51,0.14)] reveal-on-scroll"
>

                    <div className="absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#094D92] via-[#eb427e] to-[#EDA60C]" />

                    <div className="relative overflow-hidden border-b border-[#094D92]/10 bg-[linear-gradient(135deg,#f7fbff_0%,#ffffff_48%,#fff4f8_100%)] p-6 text-center sm:p-9">
                        <div className="absolute left-8 top-8 h-24 w-24 rounded-full bg-[#094D92]/8 blur-2xl" />
                        <div className="absolute bottom-4 right-10 h-28 w-28 rounded-full bg-[#eb427e]/10 blur-2xl" />
                        <div className="absolute left-1/2 top-0 h-px w-56 -translate-x-1/2 bg-gradient-to-r from-transparent via-[#094D92]/30 to-transparent" />

                        <div className="relative mx-auto max-w-3xl">
                            <p className="inline-flex items-center gap-2 rounded-full border border-[#094D92]/15 bg-white px-4 py-2 text-xs font-black uppercase tracking-wide text-[#094D92] shadow-[0_10px_24px_rgba(9,77,146,0.08)]">
                                <Star size={15} className="text-[#EDA60C]" />
                                Rate our services
                            </p>

                            <h2 className="mt-5 text-[2rem] font-black leading-[1.08] text-[#202938] sm:text-[2.55rem]">
                                <span className="block">Tell us how your</span>
                                <span className="block bg-gradient-to-r from-[#094D92] via-[#eb427e] to-[#EDA60C] bg-clip-text text-transparent">
                                    experience felt
                                </span>
                            </h2>

                            <p className="mx-auto mt-4 max-w-2xl text-sm font-semibold leading-7 text-slate-600 sm:text-base">
                                Choose a score, select the areas that matter most, and share a short note. Your response helps us improve the platform with clarity.
                            </p>
                        </div>
                    </div>

                    <div className="p-5 md:p-8">
                        <div className="rounded-[32px] border border-[#094D92]/10 bg-[linear-gradient(135deg,#f8fbff_0%,#ffffff_70%,#fff9fb_100%)] p-5 shadow-[0_18px_55px_rgba(9,77,146,0.08)] sm:p-6">
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                                <div>
                                    <p className="text-xs font-black uppercase tracking-wide text-[#eb427e]">
                                        Rating scale
                                    </p>
                                    <h3 className="mt-1 text-2xl font-black text-[#202938]">
                                        How did the experience feel?
                                    </h3>
                                </div>
                                <p className="text-sm font-bold text-slate-500">
                                    1 = Low, 10 = Excellent
                                </p>
                            </div>

                            <div className="mt-5 grid grid-cols-5 gap-2 sm:grid-cols-10">
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => {
                                            setRating(value)
                                        }}
                                        className={`group flex aspect-square items-center justify-center rounded-2xl border text-lg font-black transition duration-200 ${
                                            rating === value
                                                ? 'scale-105 border-[#eb427e] bg-[#eb427e] text-white shadow-[0_16px_34px_rgba(235,66,126,0.28)]'
                                                : 'border-slate-200 bg-white text-slate-600 hover:-translate-y-1 hover:border-[#094D92] hover:text-[#094D92] hover:shadow-[0_10px_24px_rgba(9,77,146,0.1)]'
                                        }`}
                                    >
                                        {value}
                                    </button>
                                ))}
                            </div>

                            <div className="mt-5 h-3 overflow-hidden rounded-full bg-slate-100">
                                <div
                                    className="h-full rounded-full bg-gradient-to-r from-[#eb427e] via-[#EDA60C] to-emerald-500 transition-all duration-500"
                                    style={{ width: `${rating * 10}%` }}
                                />
                            </div>

                            <div className="mt-3 flex items-center justify-between text-xs font-black uppercase tracking-wide text-slate-400">
                                <span>Needs work</span>
                                <span>Excellent</span>
                            </div>

                            <div className="mt-5 flex flex-col gap-4 rounded-[26px] border border-[#094D92]/10 bg-white p-4 shadow-[0_14px_36px_rgba(9,77,146,0.08)] sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#094D92]/10 text-[#094D92]">
                                        <Star size={19} className="text-[#EDA60C]" />
                                    </div>
                                    <div>
                                        <p className="text-xs font-black uppercase tracking-wide text-slate-400">
                                            Selected rating
                                        </p>
                                        <p className="mt-1 text-sm font-black text-[#094D92]">
                                            {ratingLabel}
                                        </p>
                                    </div>
                                </div>

                                <p className="text-4xl font-black leading-none text-[#202938]">
                                    {rating || '--'}<span className="text-base text-slate-400">/10</span>
                                </p>
                            </div>
                        </div>

                        <div className="mt-6 rounded-[30px] border border-slate-200 bg-white p-5 shadow-[0_14px_42px_rgba(31,38,51,0.06)] sm:p-6">
                            <p className="text-xs font-black uppercase tracking-wide text-[#094D92]">
                                Your details
                            </p>

                            <div className="mt-4 grid gap-4 md:grid-cols-2">
                                <input
                                    type="text"
                                    name="fullName"
                                    value={form.fullName}
                                    onChange={handleInputChange}
                                    placeholder="Full name"
                                    className="h-[3.35rem] rounded-2xl border border-slate-200 bg-[#f8fafc] px-4 font-semibold outline-none transition focus:border-[#094D92] focus:bg-white focus:shadow-[0_10px_24px_rgba(9,77,146,0.08)]"
                                />

                                <input
                                    type="tel"
                                    name="phoneNumber"
                                    value={form.phoneNumber}
                                    onChange={handleInputChange}
                                    placeholder="Phone number"
                                    className="h-[3.35rem] rounded-2xl border border-slate-200 bg-[#f8fafc] px-4 font-semibold outline-none transition focus:border-[#094D92] focus:bg-white focus:shadow-[0_10px_24px_rgba(9,77,146,0.08)]"
                                />

                                <input
                                    type="email"
                                    name="email"
                                    value={form.email}
                                    onChange={handleInputChange}
                                    placeholder="Email address"
                                    className="h-[3.35rem] rounded-2xl border border-slate-200 bg-[#f8fafc] px-4 font-semibold outline-none transition focus:border-[#094D92] focus:bg-white focus:shadow-[0_10px_24px_rgba(9,77,146,0.08)]"
                                />

                                <input
                                    type="text"
                                    name="city"
                                    value={form.city}
                                    onChange={handleInputChange}
                                    placeholder="City"
                                    className="h-[3.35rem] rounded-2xl border border-slate-200 bg-[#f8fafc] px-4 font-semibold outline-none transition focus:border-[#094D92] focus:bg-white focus:shadow-[0_10px_24px_rgba(9,77,146,0.08)]"
                                />
                            </div>
                        </div>

                        <div className="mt-6 rounded-[30px] border border-slate-200 bg-[#fbfdff] p-5 shadow-[0_14px_42px_rgba(31,38,51,0.04)] sm:p-6">
                            <p className="mb-3 text-sm font-black uppercase tracking-wide text-[#1f2633]">
                                Which parts matter most to you?
                            </p>

                            <div className="flex flex-wrap gap-2">
                                {featureOptions.map((feature) => (
                                    <button
                                        key={feature}
                                        type="button"
                                        onClick={() => handleFeatureToggle(feature)}
                                        className={`rounded-full border px-4 py-2 text-sm font-bold transition ${
                                            selectedFeatures.includes(feature)
                                                ? 'border-[#094D92] bg-[#094D92] text-white shadow-[0_10px_22px_rgba(9,77,146,0.18)]'
                                                : 'border-slate-200 bg-white text-slate-600 hover:border-[#094D92] hover:text-[#094D92] hover:shadow-[0_8px_18px_rgba(9,77,146,0.08)]'
                                        }`}
                                    >
                                        {feature}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="mt-6 rounded-[30px] border border-slate-200 bg-white p-5 shadow-[0_14px_42px_rgba(31,38,51,0.06)] sm:p-6">
                            <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <label className="block text-sm font-black uppercase tracking-wide text-[#1f2633]">
                                    {isLowRating
                                        ? 'Tell us what issue you faced'
                                        : 'Tell us what you liked or what we can improve'}
                                </label>

                                <button
                                    type="button"
                                    onClick={handleVoiceInput}
                                    disabled={!voiceSupported}
                                    className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-sm font-black transition ${
                                        isListening
                                            ? 'bg-[#eb427e] text-white shadow-[0_14px_30px_rgba(235,66,126,0.22)] hover:-translate-y-0.5'
                                            : 'border border-[#094D92]/15 bg-[#f3f8ff] text-[#094D92] hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_12px_26px_rgba(9,77,146,0.12)]'
                                    } disabled:cursor-not-allowed disabled:opacity-55`}
                                >
                                    {isListening ? <MicOff size={17} /> : <Mic size={17} />}
                                    {isListening ? 'Stop Mic' : 'Use Mic'}
                                </button>
                            </div>

                            <textarea
                                name="feedbackMessage"
                                value={form.feedbackMessage}
                                onChange={handleInputChange}
                                rows={6}
                                placeholder={
                                    isLowRating
                                        ? 'Please explain the issue, confusion, delay, missing feature or support problem.'
                                        : 'Please share what you liked about our platform, services, support or workflow.'
                                }
                                className="w-full resize-none rounded-[24px] border border-slate-200 bg-[#f8fafc] p-4 font-semibold leading-7 outline-none transition focus:border-[#094D92] focus:bg-white focus:shadow-[0_12px_30px_rgba(9,77,146,0.08)]"
                            />

                            {!voiceSupported && (
                                <p className="mt-3 text-xs font-bold text-slate-500">
                                    Voice typing works best in Chrome or Edge with microphone permission enabled.
                                </p>
                            )}
                        </div>

                        {status.message && (
                            <div
                                className={`mt-5 flex items-start gap-3 rounded-2xl p-4 font-bold ${
                                    status.type === 'success'
                                        ? 'bg-emerald-50 text-emerald-700'
                                        : 'bg-rose-50 text-rose-700'
                                }`}
                            >
                                {status.type === 'success' ? (
                                    <CheckCircle2 className="mt-0.5 shrink-0" size={20} />
                                ) : (
                                    <CircleAlert className="mt-0.5 shrink-0" size={20} />
                                )}
                                <span>{status.message}</span>
                            </div>
                        )}

                       <div className="mt-7 flex flex-col items-center justify-center gap-3">
    <button
        type="submit"
        disabled={loading}
        className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-[#eb427e] to-[#094D92] px-8 py-3 font-black text-white shadow-[0_16px_35px_rgba(9,77,146,0.22)] transition hover:-translate-y-0.5 hover:shadow-[0_20px_42px_rgba(9,77,146,0.28)] disabled:cursor-not-allowed disabled:opacity-60"
    >
        {loading ? 'Submitting...' : 'Submit Feedback'}
    </button>

</div>


                        {isHighRating && (
                            <div className="mt-7 rounded-[30px] border border-emerald-200 bg-[linear-gradient(135deg,#ecfdf5_0%,#ffffff_100%)] p-5 shadow-[0_18px_45px_rgba(16,185,129,0.12)]">
                                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                                    <div>
                                        <p className="inline-flex items-center gap-2 text-sm font-black uppercase tracking-wide text-emerald-700">
                                            <ThumbsUp size={17} />
                                            Glad you liked it
                                        </p>
                                        <h3 className="mt-2 text-2xl font-black text-[#1f2633]">
                                            Continue with {surveyLabel} registration
                                        </h3>
                                        <p className="mt-2 text-sm font-semibold leading-6 text-slate-600">
                                            You can continue to the official registration page after sharing your feedback.
                                        </p>
                                    </div>

                                    <a
                                        href={getServerUrl(registrationLinks[surveyType])}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center justify-center gap-2 rounded-full bg-[#094D92] px-6 py-3 font-black text-white shadow-[0_14px_30px_rgba(9,77,146,0.2)] transition hover:-translate-y-0.5 hover:bg-[#073b70]"
                                    >
                                        Open {surveyLabel} Registration
                                        <ExternalLink size={17} />
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                </form>
            </section>
        </main>
    )
}

export default FeedbackSurvey
